import type { CompanySafetyJsaAnalyticsRange, CompanySafetyInspectionRecord } from '../web/services/jobsService';
import type { SafetyIncidentCase } from './safetyIncidents';
import { safetyTimestamp } from './safetyMonitoring';
export type SafetyAnalyticsRange = CompanySafetyJsaAnalyticsRange;
const DAY = 86400000;
export function analyticsWindow(range: SafetyAnalyticsRange, now = Date.now()) {
  const current = new Date(now);
  const today = Date.UTC(current.getUTCFullYear(), current.getUTCMonth(), current.getUTCDate());
  return { start: range === 'all' ? null : range === 'ytd' ? Date.UTC(current.getUTCFullYear(), 0, 1) : today - (range === '30d' ? 29 : 89) * DAY, end: today + DAY };
}
export function analyticsTime(value?: string | null): number {
  if (!value) return NaN;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(date) && new Date(date).toISOString().slice(0, 10) === value ? date : NaN;
  }
  return safetyTimestamp(value);
}
export function analyticsInRange(value: string | null | undefined, range: SafetyAnalyticsRange, now = Date.now()) {
  const time = analyticsTime(value); const window = analyticsWindow(range, now);
  return Number.isFinite(time) && time < window.end && (window.start === null || time >= window.start);
}
export function analyticsIncidentDate(item: SafetyIncidentCase) { return item.report.eventDate || item.report.recordedAt; }
export function analyticsIncidents(cases: SafetyIncidentCase[], range: SafetyAnalyticsRange, now = Date.now()) {
  return cases.filter(item => analyticsInRange(analyticsIncidentDate(item), range, now));
}
export function analyticsHighPotential(item: SafetyIncidentCase) { return ['high', 'critical'].includes(item.potential); }
export function analyticsBreakdown(cases: SafetyIncidentCase[], field: 'crew' | 'locationFacility') {
  const groups = new Map<string, { name: string; count: number; high: number; ids: string[] }>();
  for (const item of cases) {
    const name = item.report[field]?.trim() || (field === 'crew' ? 'Unspecified crew' : 'Unspecified location');
    const group = groups.get(name) || { name, count: 0, high: 0, ids: [] };
    group.count++; group.high += Number(analyticsHighPotential(item)); group.ids.push(item.id); groups.set(name, group);
  }
  return [...groups.values()].sort((a, b) => b.high - a.high || b.count - a.count || a.name.localeCompare(b.name));
}
export function inspectionDefectNoted(record: Pick<CompanySafetyInspectionRecord, 'defects'>) {
  const text = String(record.defects || '').trim().toLowerCase().replace(/[.!]+$/, '');
  return Boolean(text && !['none', 'no', 'nil', 'n/a', 'na', 'no defects', 'no defects found', 'no issues', 'no issues found'].includes(text));
}
export type ActivityPoint = { date: string; count: number };
export function analyticsBuckets(points: ActivityPoint[], range: SafetyAnalyticsRange, now = Date.now()) {
  const window = analyticsWindow(range, now);
  const valid = points.filter(point => analyticsInRange(point.date, range, now));
  const start = window.start ?? (valid.length ? Math.min(...valid.map(point => analyticsTime(point.date))) : window.end - 30 * DAY);
  const days = Math.max(1, Math.ceil((window.end - start) / DAY));
  const step = days <= 35 ? 7 : days <= 90 ? 14 : Math.ceil(days / 8);
  const buckets = Array.from({ length: Math.ceil(days / step) }, (_, i) => {
    const from = start + i * step * DAY; const to = Math.min(window.end, from + step * DAY);
    const label = (time: number) => new Date(time).toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'UTC' });
    return { start: from, end: to, label: `${label(from)} – ${label(to - DAY)}`, count: 0 };
  });
  for (const point of valid) { const index = Math.floor((analyticsTime(point.date) - start) / (step * DAY)); if (buckets[index]) buckets[index].count += Number(point.count) || 0; }
  return buckets;
}
